/**
 * CardSectionSettings — Per-section inline settings for the card editor.
 *
 * Renders appropriate controls based on section type:
 * - image: ratio + hover effect
 * - title: max lines
 * - price: compare price toggle
 * - addToCart: button style
 * - vendor, rating, variants, volumePrice: no settings
 */

import { PresetControl } from './controls'
import type { CardSection } from './card-editor-types'
import {
  IMAGE_RATIO_OPTIONS,
  IMAGE_HOVER_OPTIONS,
  TITLE_LINES_OPTIONS,
  COMPARE_PRICE_OPTIONS,
  BUTTON_STYLE_OPTIONS,
} from './card-editor-types'

interface CardSectionSettingsProps {
  section: CardSection
  onConfigChange: (key: string, value: string) => void
}

export function CardSectionSettings({ section, onConfigChange }: CardSectionSettingsProps) {
  switch (section.type) {
    case 'image':
      return (
        <div className="space-y-3 rounded-sm bg-gray-50 p-3">
          <PresetControl
            value={section.config['data-image-ratio'] ?? '1:1'}
            onChange={(v) => onConfigChange('data-image-ratio', v)}
            label="Image Ratio"
            options={IMAGE_RATIO_OPTIONS}
          />
          <PresetControl
            value={section.config['data-hover-effect'] ?? 'zoom'}
            onChange={(v) => onConfigChange('data-hover-effect', v)}
            label="Hover Effect"
            options={IMAGE_HOVER_OPTIONS}
          />
        </div>
      )

    case 'title':
      return (
        <div className="rounded-sm bg-gray-50 p-3">
          <PresetControl
            value={section.config['data-title-lines'] ?? '2'}
            onChange={(v) => onConfigChange('data-title-lines', v)}
            label="Max Lines"
            options={TITLE_LINES_OPTIONS}
          />
        </div>
      )

    case 'price':
      return (
        <div className="rounded-sm bg-gray-50 p-3">
          <PresetControl
            value={section.config['data-show-compare-price'] ?? 'false'}
            onChange={(v) => onConfigChange('data-show-compare-price', v)}
            label="Compare Price"
            options={COMPARE_PRICE_OPTIONS}
          />
        </div>
      )

    case 'addToCart':
      return (
        <div className="rounded-sm bg-gray-50 p-3">
          <PresetControl
            value={section.config['data-button-style'] ?? 'filled'}
            onChange={(v) => onConfigChange('data-button-style', v)}
            label="Button Style"
            options={BUTTON_STYLE_OPTIONS}
          />
        </div>
      )

    default:
      return null
  }
}
